import { toJS } from 'mobx';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import { deliveryAddressStore } from '~/components/ModalDeliveryAddress/DeliveryAddressStore';
import {
  saveAuthorizedAddress,
  saveUnauthorizedAddress,
  sendAddressChangedAnalytics,
  sendOnboardingAnalytics,
  sendPostSaveAnalytics,
  updateEtaAndDeliveryMethod,
} from '~/components/ModalDeliveryAddress/saveAddressUtils';

import { mainStore, orderStore, userStore } from '~/stores';

import { useModal } from './useModal';

interface UseSaveAddressOptions {
  shouldValidateBuilding?: boolean;
  onClick?: (addressId: string) => void;
}

export const useSaveAddress = ({
  shouldValidateBuilding,
  onClick,
}: UseSaveAddressOptions = {}) => {
  const { t } = useTranslation();
  const { goBack } = useModal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPickup =
    searchParams.get('pickup') === 'true' && !!company.pickupAddress;

  const handleSaveAddress = async () => {
    deliveryAddressStore.setIsLoading(true);
    try {
      const deliveryAddress = toJS(deliveryAddressStore.deliveryAddress);
      const editAddressData = toJS(deliveryAddressStore.editAddressData);

      if (!deliveryAddress?.city) {
        mainStore.pushAlert('error', t('phrases:determineAddress'));
        return;
      }

      if (!deliveryAddressStore.etaCalculation && !isPickup) {
        mainStore.pushAlert(
          'error',
          "Your address isn't in a delivery zone yet",
        );
        return;
      }

      sendAddressChangedAnalytics(
        deliveryAddress,
        deliveryAddressStore.etaCalculation,
      );

      let addressId = '';
      let savedAddress = deliveryAddress;

      if (userStore.isAuthorized) {
        const result = await saveAuthorizedAddress(
          deliveryAddress,
          editAddressData,
        );
        addressId = result.addressId;
        savedAddress = result.updatedDeliveryAddress;
      } else {
        saveUnauthorizedAddress(deliveryAddress, editAddressData);
      }

      await updateEtaAndDeliveryMethod(isPickup);

      sendPostSaveAnalytics(savedAddress);

      if (userStore.isFirstLaunch) {
        sendOnboardingAnalytics(
          savedAddress,
          deliveryAddressStore.etaCalculation,
        );

        if (
          !isPickup &&
          (!deliveryAddressStore.deliveryAddress ||
            !(deliveryAddressStore.etaCalculation ?? orderStore.etaCalculation))
        ) {
          mainStore.pushAlert(
            'error',
            "Your address isn't in a delivery zone yet",
          );
        }
        if (onClick) {
          onClick(addressId);
        } else {
          goBack();
        }
        return;
      }

      if (mainStore.deferedDeeplink) {
        navigate(mainStore.deferedDeeplink, {
          state: { isDeeplink: true },
        });
        mainStore.setDeferedDeeplink('');
        return;
      }

      if (onClick) {
        onClick(addressId);
      } else {
        goBack();
      }
    } finally {
      deliveryAddressStore.setIsLoading(false);
    }
  };

  const isDisabled =
    !deliveryAddressStore.deliveryAddress ||
    (!shouldValidateBuilding &&
      !deliveryAddressStore.editAddressData.addressBld.trim()) ||
    deliveryAddressStore.inputAddressFocused ||
    deliveryAddressStore.isLoading ||
    orderStore.isETALoading ||
    (!userStore.isFirstLaunch &&
      !deliveryAddressStore.etaCalculation &&
      !isPickup);

  return {
    handleSaveAddress,
    isLoading: deliveryAddressStore.isLoading,
    isDisabled,
  };
};
